import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
} from '@angular/core';
import { assert, isDefined } from '@sama/js-core';
import { DirectiveSuperclass } from '@sama/ng-core';
import { get } from 'lodash';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { customIcon } from '../../../cross-team-shared/icons/register-custom-icons';
import { CuboidLocationState3D } from '../../state/cuboid-location-state-3d';
import { PointCloudContentStore } from '../../state/point-cloud-content-store';
import { deleteIfDefined, MeasurementMode } from '../../state/render-state';
import {
  calculateFreePointDistance,
  calculateSubpointHeightDistance,
} from '../../tools/measure-tool/measure-utils';
import {
  degreeAngle,
  distanceCuboidToPoint,
  distanceFreePoint,
  distanceGroundToPoint,
} from '../../utils/custom-icons';
import { MouseCoordinateService } from '../../utils/mouse-coordinate.service';
import { distanceBetween } from '../../utils/point-utils';
import {
  angleBetween,
  getSensorDirection,
  getXYDirectionToObject,
} from './angle-utils';

function radiansToDegree(radians: number) {
  return (radians * 180) / Math.PI;
}

interface FormattedMeasurementValues {
  angle?: string;
  distance: string;
  mode: MeasurementMode;
}

@Component({
  selector: 'h3-measure-toolbar',
  templateUrl: './measure-toolbar.component.html',
  styleUrls: ['./measure-toolbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class MeasureToolbarComponent
  extends DirectiveSuperclass
  implements OnInit
{
  formattedMeasurement$!: Observable<FormattedMeasurementValues | undefined>;

  faDistanceFreePoint = customIcon(distanceFreePoint);
  faDistanceCuboidToPoint = customIcon(distanceCuboidToPoint);
  faDistanceSubpointHeight = customIcon(distanceGroundToPoint);
  faDegreeAngle = customIcon(degreeAngle);

  visibleFocusedCuboidLocationWIthSensorAvailable$!: Observable<
    CuboidLocationState3D | undefined
  >;

  private store = inject(PointCloudContentStore);
  private mouseCoordinateService = inject(MouseCoordinateService);

  ngOnInit() {
    this.visibleFocusedCuboidLocationWIthSensorAvailable$ =
      this.store.visibleFocusedCuboidLocation$.pipe(
        this.filterIfSensorNotAvailable$.bind(this),
      );

    this.subscribeToVisibleFocusedCuboidLocation();
    this.formattedMeasurement$ = this.buildFormattedMeasurement$();
  }

  private subscribeToVisibleFocusedCuboidLocation() {
    this.subscribeTo(
      this.visibleFocusedCuboidLocationWIthSensorAvailable$,
      (cuboidLocation) => {
        const measurementModeStore = this.store('render')('measurementMode');
        if (cuboidLocation) {
          measurementModeStore.set('cuboid-to-sensor');
        } else if (measurementModeStore.state() === 'cuboid-to-sensor') {
          measurementModeStore.setUsing(deleteIfDefined);
        }
      },
    );
  }

  private buildFormattedMeasurement$() {
    return this.store('render')('measurementMode').$.pipe(
      switchMap((mode) => {
        switch (mode) {
          case 'free-point':
            return this.buildFreePointMeasurement$();
          case 'cuboid-to-sensor':
            return this.buildCuboidToSensorMeasurement$();
          case 'subpoint-height':
            return this.buildSubPointHeightMeasurement$();
          default:
            return of(undefined);
        }
      }),
      map(this.getFormattedMeasurement.bind(this)),
    );
  }

  private getFormattedMeasurement(measurement?: {
    angle?: number;
    distance: number;
  }): FormattedMeasurementValues | undefined {
    if (!isDefined(measurement)) {
      return undefined;
    }

    let angle: string | undefined;
    if (isDefined(measurement.angle)) {
      angle = this.formatAngle(measurement.angle);
    }

    const { measurementMode } = this.store.state().render;
    assert(isDefined(measurementMode), 'Measurement mode should be defined');

    return {
      angle,
      distance: this.formatDistance(measurement.distance),
      mode: measurementMode,
    };
  }

  private formatDistance(distance: number) {
    return distance.toFixed(2);
  }

  /** Angle uses the unicode U+00B0 (degree sign) */
  private formatAngle(angle: number) {
    let formattedValue = '-';

    if (!isNaN(angle)) {
      const orientation = angle <= 0 ? 'R' : 'L';
      const angleInDegrees = radiansToDegree(angle);
      const roundedAngle = Math.abs(angleInDegrees).toFixed(2);
      formattedValue = `${roundedAngle}° - ${orientation}`;
    }

    return formattedValue;
  }

  private buildFreePointMeasurement$() {
    return combineLatest([
      this.store('render')('freePointMeasurement').$.pipe(filter(isDefined)),
      this.store('mouse')('containerPoint').$,
    ]).pipe(
      filter(([measurement]) => measurement.active),
      map(() => ({
        distance: calculateFreePointDistance(
          this.store.state(),
          this.mouseCoordinateService.getGroundedMousePosition(),
        ),
      })),
    );
  }

  private buildCuboidToSensorMeasurement$() {
    return this.visibleFocusedCuboidLocationWIthSensorAvailable$.pipe(
      map((cuboidLocation) => {
        const location = get(
          this.store.state().locations,
          this.store.state().render.frameNumber,
        );
        if (!cuboidLocation || !location) {
          return undefined;
        }

        const target = cuboidLocation.position;
        return {
          angle: angleBetween(
            getSensorDirection(location.rotation),
            getXYDirectionToObject(target, location.position),
          ),
          distance: distanceBetween(target, location.position),
        };
      }),
    );
  }

  private buildSubPointHeightMeasurement$() {
    return this.store('render')('subpointHeightMeasurement')
      .$.pipe(filter(isDefined))
      .pipe(
        map(() => ({
          distance: calculateSubpointHeightDistance(this.store.state()),
        })),
      );
  }

  private filterIfSensorNotAvailable$<T>(source$: Observable<T>) {
    return source$.pipe(
      filter(() => {
        const location = get(
          this.store.state().locations,
          this.store.state().render.frameNumber,
        );
        const showSensorLocation =
          this.store.state().output.meta.show_3d_distance_location;
        return showSensorLocation === true && isDefined(location);
      }),
    );
  }
}
